/**
 * Who the visitor is, which thread they are in, and what they chose.
 *
 * `ph.uid` outlives «Новый диалог» on purpose: it is what carries a person
 * across conversations, and it is the field `docs/UI_CONTRACT.md` §4.1 asks
 * every frontend to assert. Without it the graph falls back to `web:<thread_id>`
 * — narrower than nothing, but it forgets the visitor every time they start
 * over, which is exactly what a guide that remembers a name must not do.
 */

import { isAgentFamily } from './graphFamily';
import { create } from 'zustand';
import { useNavigation } from '../location/navigationStore';
import { uuid4 } from '../lib/ids';
import type { DeployModels } from '../transport/api';

export type ThemeChoice = 'system' | 'light' | 'dark';

export type ModelChoice = {
  id: string;
  label: string;
  /** `undefined` = send no `model` at all — «as the instance is configured». */
  model?: string;
  /** A sampling temperature this row runs at; absent = the graph's own (no row sets one today). */
  temperature?: number;
  /**
   * The reasoning effort this ROW runs at — set only on the variants
   * `withReasoning` makes for a model that has the parameter (owner,
   * 2026-09-22: «в моделях прописывать, а не в отдельном блоке»). Absent on a
   * model without it (GigaChat): no effort is sent at all.
   */
  reasoning?: ReasoningLevel;
};

/**
 * The id that means «no override chosen» — never a row in any choice list, so
 * a menu never has to filter one out and `.find(c => c.id === id)` naturally
 * misses (owner's request, 2026-09-16: the menu itself lists only real
 * models, since the checkmark already says which one is selected — see
 * `orchChoiceIdFor`/`modelChoiceIdFor` for how the checkmark is now decided).
 */
export const NO_OVERRIDE = '';

/**
 * The reasoning efforts a model row can carry. `off` is SENT (in the graph's
 * own spelling, `reasoningWire`) — GPT-5.6 reasons at `medium` when told
 * nothing, so «без рассуждений» has to be said.
 */
export type ReasoningLevel = 'off' | 'low' | 'medium' | 'high';
export type ReasoningChoice = { id: ReasoningLevel; label: string };

export const REASONING_CHOICES: ReasoningChoice[] = [
  { id: 'off', label: 'без рассуждений' },
  { id: 'low', label: 'low' },
  { id: 'medium', label: 'medium' },
  { id: 'high', label: 'high' },
];

/**
 * **The effort is part of the MODEL row, not a group of its own** (owner,
 * 2026-09-22). The separate «Рассуждения» groups offered an effort for every
 * model, GigaChat included, where the model builder simply drops it — a
 * checkmark that did nothing. Now a model that HAS the parameter is listed once
 * per effort («GPT-5.6 Terra · low»), and a model that has not (GigaChat) is one
 * row. Dev stand only (`VITE_PH_REASONING_SWITCH=1`); the public build
 * lists each model once and sends no effort — the deploy's own stands.
 */
export const REASONING_SWITCH = import.meta.env.VITE_PH_REASONING_SWITCH === '1';

/**
 * Models that take `reasoning_effort`, by the name we send: GPT-5.6 through
 * OpenRouter. Not GigaChat (the model builder drops it; 3 Ultra streams
 * reasoning without end with it). The open models that also took it (DeepSeek,
 * GLM) left every menu with the OpenCode gateway on 2026-10-15 (INV-PH-111).
 */
const REASONING_MODEL_PREFIXES = ['openai/gpt-5.6-'];

export function takesReasoning(model: string | undefined): boolean {
  return !!model && REASONING_MODEL_PREFIXES.some((prefix) => model.startsWith(prefix));
}

/**
 * *choices* with every reasoning-capable model spread into one row per effort —
 * `<id>:<effort>`, «<label> · <effort>» — and everything else untouched. A
 * row's `badge` (the header's short name for an override) carries the effort
 * too. With *enabled* false the list comes back as it was.
 */
export function withReasoning<T extends ModelChoice & { badge?: string }>(
  choices: T[],
  enabled: boolean = REASONING_SWITCH,
): T[] {
  if (!enabled) return choices;
  return choices.flatMap((choice) => {
    if (!takesReasoning(choice.model)) return [choice];
    // One set of variant OBJECTS per base row: the GPT rows join several lists
    // (`GIGACHAT_MODEL_CHOICES`, `ORCH_MODEL_CHOICES`), and a list that is «the
    // other list itself» should stay so by identity.
    const cached = VARIANTS.get(choice);
    if (cached) return cached as T[];
    const variants = REASONING_CHOICES.map((level) => ({
      ...choice,
      id: `${choice.id}:${level.id}`,
      label: `${choice.label} · ${level.label}`,
      reasoning: level.id,
      ...(choice.badge ? { badge: `${choice.badge} · ${level.label}` } : {}),
    }));
    VARIANTS.set(choice, variants);
    return variants;
  });
}

const VARIANTS = new WeakMap<ModelChoice, ModelChoice[]>();

/**
 * GPT-5.6 through OpenRouter (owner, 2026-09-22) — the dev stand only
 * (`VITE_PH_OPENROUTER_MODELS=1`, set with the graph switcher). Every graph
 * can run these on EITHER contour: the backend routes the NAME to OpenRouter
 * inside the one client builder every graph shares
 * (`peterhof_guide.openrouter_route`), whatever endpoint the contour is
 * configured with — so the same two rows join every list below. No
 * temperature: OpenRouter does not take one for these models.
 */
export const OPENROUTER_MODELS = import.meta.env.VITE_PH_OPENROUTER_MODELS === '1';

export const OPENROUTER_MODEL_CHOICES: ModelChoice[] = [
  { id: 'gpt-terra', label: 'GPT-5.6 Terra', model: 'openai/gpt-5.6-terra' },
  { id: 'gpt-luna', label: 'GPT-5.6 Luna', model: 'openai/gpt-5.6-luna' },
];

/** The OpenRouter rows when this build offers them, else nothing. */
export function openrouterChoices(enabled: boolean = OPENROUTER_MODELS): ModelChoice[] {
  return enabled ? OPENROUTER_MODEL_CHOICES : [];
}

/**
 * **The open-model rows are gone** (owner, 2026-10-15, INV-PH-111): deepseek,
 * GLM and Kimi were served only by the OpenCode gateway, which left the
 * platform, and the graphs that ran them left the registry with it. What every
 * menu lists now is GigaChat through the internal gateway, plus the GPT rows on
 * the dev stand (`openrouterChoices`).
 *
 * The list is `ORCH_MODEL_CHOICES`' own generations, in its order (owner,
 * 2026-09-16). GigaChat-2-Pro was probed live in the dev container that day and
 * answers 200; `GET /models` also lists GigaChat-3-Pro and -Lightning, and both
 * answered 404 «No such model» then. The live graphs list cloud.ru's models
 * instead (`CLOUDRU_MODEL_CHOICES`).
 */
export const GIGACHAT_MODEL_CHOICES: ModelChoice[] = withReasoning([
  { id: 'giga2-max', label: 'GigaChat 2 Max', model: 'openai/GigaChat-2-Max' },
  { id: 'giga2-pro', label: 'GigaChat 2 Pro', model: 'openai/GigaChat-2-Pro' },
  { id: 'giga3-ultra', label: 'GigaChat 3 Ultra', model: 'openai/GigaChat-3-Ultra' },
  ...openrouterChoices(),
]);

/**
 * **The live graphs' menu is cloud.ru Foundation Models' GigaChat** (owner,
 * 23.10.2026), reached through the internal gateway's default path — its
 * `cloudru` upstream, the only path Peterhof's calls take (owner, 17.10.2026).
 * Every row answered 200 through the gateway that day; the old rows
 * (`GIGACHAT_MODEL_CHOICES`: 2 Max, 2 Pro, 3 Ultra on Sber's own upstreams)
 * answered 401/402, and the 3.5 row — the deploy's default — was missing from
 * Guide v2's list, so a row picked once could not be cleared: tapping the row
 * that equals the default is what clears a pin. The ids are cloud.ru's own
 * (`ai-sage/…`, `GigaChat/…`); 2 Max is in cloud.ru's catalog but not routed by
 * the gateway (`GATEWAY_CLOUDRU_MODELS`), so it is not listed. 3.5 Reasoning and
 * 3 Lightning (`ai-sage/GigaChat3-10B-A1.8B`) were listed for a day and left
 * (owner, 23.10.2026): on the live turns Reasoning answered the Agent with
 * nothing after 77 s and v2 after 124 s, and Lightning answered v2 with its own
 * system prompt. No effort rows: neither GigaChat row has the parameter.
 */
export const CLOUDRU_MODEL_CHOICES: ModelChoice[] = withReasoning([
  { id: 'giga35', label: 'GigaChat 3.5', model: 'openai/ai-sage/GigaChat3.5-432B-A28B' },
  { id: 'giga3-pro-cloudru', label: 'GigaChat 3 Pro', model: 'openai/GigaChat/GigaChat-3-Pro' },
  ...openrouterChoices(),
]);

/**
 * **The SECOND contour's models** — the orchestrators' helper and the tandem's
 * background worker (owner, 2026-09-16: one list for all of them). Since the
 * open half left with the OpenCode gateway (2026-10-15, INV-PH-111) it is the
 * GigaChat list itself; the delegation graphs that read it are no longer in the
 * registry, so no reachable graph offers it today (`helperChoicesFor`).
 *
 * `NO_OVERRIDE` sends no field at all, which leaves the deploy's own setting in
 * charge, and the list has no «По умолчанию» ROW (owner, 2026-09-16): the
 * checkmark stands on whichever model the deploy runs (`workerModelInUseFor`),
 * and tapping that item again clears the override rather than pinning it.
 */
export const HELPER_MODEL_CHOICES: ModelChoice[] = GIGACHAT_MODEL_CHOICES;

/**
 * The SECOND contour's presets for a graph, or `[]` for a graph this menu
 * cannot point anywhere.
 *
 * **`peterhof_live` is that `[]`, and deliberately.** Its helper is a separate
 * RUN of a separate graph rather than a client built inside the turn, so
 * pointing it somewhere means forwarding a model into the job the way
 * `peterhof_tandem` forwards one — a wave of its own. The header still NAMES
 * what that contour runs (`workerModelInUseFor`); it just offers no rows.
 */
export function helperChoicesFor(assistantId: string): ModelChoice[] {
  if (assistantId === 'peterhof_tandem' || isOrchGraph(assistantId)) return HELPER_MODEL_CHOICES;
  return [];
}

/** Does this graph run a second circuit whose model is switchable per run? */
export function hasWorkerModelSwitch(assistantId: string): boolean {
  return helperChoicesFor(assistantId).length > 0;
}

/**
 * The presets a graph can actually run: the two graphs the registry serves
 * (`KNOWN_GRAPHS`) get the cloud.ru list. Any other id gets the old GigaChat
 * list — none is served since the scripted Telegram guide left the registry
 * (2026-10-16, INV-PH-111).
 */
export function modelChoicesFor(assistantId: string): ModelChoice[] {
  return KNOWN_GRAPHS.includes(assistantId) ? CLOUDRU_MODEL_CHOICES : GIGACHAT_MODEL_CHOICES;
}

const KEY = {
  uid: 'ph.uid',
  thread: 'ph.thread',
  /** The graph `ph.thread` was started under — a checkpoint of one graph is not another's history. */
  threadGraph: 'ph.threadGraph',
  theme: 'ph.theme',
  geo: 'ph.geo',
  orchModel: 'ph.orchModel',
  workerModel: 'ph.workerModel',
  /** The last erase this browser acted on (`acknowledgeErase`), so a reload does not rotate again. */
  forgot: 'ph.forgot',
} as const;

/** Private mode and locked-down browsers throw on access, not on read. */
function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* a session that cannot persist still works, it just does not remember */
  }
}

/**
 * **The first-contour model choice is kept PER GRAPH** — `ph.model.<assistantId>`
 * (#139). One shared `ph.model` let a row picked once on one graph ride onto
 * every other graph whose list happened to carry the same id: «GigaChat 3 Ultra»
 * chosen on the tandem (or on the Agent, weeks ago) went out as `context.model`
 * on every Agent turn after the deploy had moved to 3.5, and the Agent — which
 * honours a named model — answered 402 on the stand. A graph now sends a model
 * only when the visitor picked it ON THAT GRAPH.
 */
export function modelKey(assistantId: string): string {
  return `ph.model.${assistantId}`;
}

/** The shared key before #139. Never read again — removed on start-up. */
export const LEGACY_MODEL_KEY = 'ph.model';

/**
 * The stored choice for *assistantId*, or `NO_OVERRIDE`. Drops the legacy
 * shared key on the way: its value cannot say which graph it was picked on, so
 * it is not migrated anywhere — a visitor who wants a model other than the
 * deploy's picks it again, on the graph they are on.
 */
export function initialModelChoice(assistantId: string): string {
  writeLocal(LEGACY_MODEL_KEY, null);
  return readLocal(modelKey(assistantId)) ?? NO_OVERRIDE;
}

/**
 * **The erase the server CONFIRMED, from a `values` frame** (asst-peterhof-guide#193):
 * `forgotten = {id, at, store}` is written by `forget_me` on «Да», and `store: true` only
 * when the database confirmed that everything stored about the visitor is gone. That is
 * the one signal a new visitor key may follow: a failed erase keeps the old key, so a
 * repeated «Забыть меня» still reaches the same rows. Returns the erase's id, or `null`.
 */
/** Erases this page has acted on — the dedupe that holds when `localStorage` is blocked. */
const acknowledgedErases = new Set<string>();

export function confirmedErase(values: unknown): string | null {
  if (!values || typeof values !== 'object') return null;
  const forgotten = (values as { forgotten?: unknown }).forgotten;
  if (!forgotten || typeof forgotten !== 'object') return null;
  const { id, store } = forgotten as { id?: unknown; store?: unknown };
  return store === true && typeof id === 'string' && id ? id : null;
}

/**
 * The browser's thread for *graph*: the kept one when that graph started it, a fresh one otherwise. 20.10.2026 the
 * landing graph changed from the Agent to Guide v2 — a returning visitor's thread is the Agent's (a thread kept
 * before this key existed is the Agent's, the only graph prod served), and v2 must not resume its checkpoint.
 */
export function threadFor(graph: string): string {
  const owner = readLocal(KEY.threadGraph) ?? 'peterhof_agent';
  if (owner !== graph) writeLocal(KEY.thread, null);
  writeLocal(KEY.threadGraph, graph);
  return ensure(KEY.thread, uuid4);
}

function ensure(key: string, make: () => string): string {
  const existing = readLocal(key);
  if (existing) return existing;
  const created = make();
  writeLocal(key, created);
  return created;
}

/**
 * `?graph=` picks which of the service's graphs answers — and since 2026-10-16
 * there is one, the Agent. The scripted Telegram guide (`peterhof_guide`) left
 * the registry that day (owner, INV-PH-111); the canvas, the duo and the
 * delegation graphs (orch, harness, live, tandem) ran open models through the
 * OpenCode gateway and left with it on 2026-10-15. Their ids are no longer
 * served, so a stale link with one of them lands on the build's landing graph
 * instead. The DEV stand
 * (peterhof-dev.aiagentslab.ru) is built with `VITE_PH_GRAPH_SWITCH=1` and offers
 * `GRAPH_CHOICES` in the settings menu.
 *
 * Exported (and taking `search` rather than reading `window.location` itself)
 * so a test can drive it with a literal query string instead of mocking
 * `window` — this module runs under vitest's `node` environment, which has no
 * `window` at all outside the try/catch below.
 */
export function graphFromUrl(search?: string, fallback: string = landingGraph()): string {
  try {
    const requested = new URLSearchParams(search ?? window.location.search).get('graph');
    if (requested && KNOWN_GRAPHS.includes(requested)) return requested;
  } catch {
    /* no URL to read (tests) */
  }
  return fallback;
}

/**
 * The graphs every build serves: Guide v2 (`peterhof_v2`, `docs/GUIDE_V2.md`) — the landing graph
 * since 20.10.2026 (owner: «v2 — основной граф», CI `PH_LANDING_GRAPH`) — and the Agent beside it,
 * one menu row or `?graph=peterhof_agent` away (INV-PH-111). It was dev-only from 17.10.
 */
const KNOWN_GRAPHS = ['peterhof_agent', 'peterhof_v2'];

/**
 * Is this one of the ORCHESTRATED graphs — the guide that talks with a helper
 * behind `delegate`?
 *
 * `peterhof_orch_harness` is `peterhof_orch` with that helper rebuilt on the
 * Deep Agents harness, and the chat looks identical by design: one model the
 * visitor reads, no tool-step feed, the same board. Three predicates below used
 * to name `peterhof_orch` by literal, so shipping the A/B's other arm would have
 * given it a DIFFERENT chat — which is the one thing an A/B may not do. Every
 * one of them asks this instead.
 */
export function isOrchGraph(assistantId: string): boolean {
  return assistantId === 'peterhof_orch' || assistantId === 'peterhof_orch_harness';
}

/** `peterhof_live` — the ninth/tenth graph pair (2026-09-16): the orchestrator
 *  doesn't block on `delegate`, the helper (Deep Agents) works on a side
 *  thread, and its result arrives as a SEPARATE message once the visitor goes
 *  idle, rather than inside the turn that started it. */
export function isLiveGraph(assistantId: string): boolean {
  return assistantId === 'peterhof_live';
}

/**
 * Does this graph use the orchestrator's OWN model-switcher shape — an
 * `ORCH_MODEL_CHOICES` group for «Первый контур», a read-only «Второй контур»
 * row naming the helper? `peterhof_live`'s first contour is built the same
 * way `peterhof_orch`'s is (a GigaChat orchestrator reached through the same
 * `PO_LLM_*`-shaped gateway, switchable per run via `context.model`) even
 * though it is not `isOrchGraph` — a different helper ENGINE and a different
 * chat surface (no turn-cards, no activity-feed change of its own — see
 * `showsActivityFeed`/`showsTurnCards` below, which do NOT route through this
 * predicate because Live's own reasons for each differ from the orchestrator's).
 */
export function usesOrchModelMenu(assistantId: string): boolean {
  return isOrchGraph(assistantId) || isLiveGraph(assistantId);
}

/**
 * The graph a URL with no `?graph=` opens. The public build leaves it at
 * `DEFAULT_ASSISTANT`; the dev build sets `VITE_PH_DEFAULT_GRAPH`. A value that
 * names no known graph is ignored — a typo in a CI variable must not strand
 * the stand on a graph id the service does not have.
 */
export function landingGraph(configured: string | undefined = import.meta.env.VITE_PH_DEFAULT_GRAPH): string {
  return configured && KNOWN_GRAPHS.includes(configured) ? configured : DEFAULT_ASSISTANT;
}

/** Only the dev build shows the graph switcher (`VITE_PH_GRAPH_SWITCH=1`). */
export const GRAPH_SWITCH = import.meta.env.VITE_PH_GRAPH_SWITCH === '1';

/**
 * A per-run override of the ORCHESTRATOR model, verified live: sending
 * `context.model` on `POST /threads/{id}/runs/stream` switches the talking
 * model of `peterhof_orch`/`peterhof_orch_harness` for that run — the deploy
 * default (`PO_MODEL` = GigaChat-2-Max) applies whenever it is absent.
 *
 * **It rides `context`, not `config.configurable`.** The first cut of this
 * switch sent `config: {configurable: {model: …}}` beside `context`, and every
 * such run answered `400 bad_request "Cannot specify both configurable and
 * context. Prefer setting context alone…"` — reagent refuses the two together,
 * and this frontend always sends `context` (`user_id`, `channel`). Verified
 * live both ways, 2026-09-16: `context.model` alone switches the orchestrator
 * (the stream's `run_meta` shows `model_name: GigaChat-3-Ultra…`) exactly like
 * `config.configurable.model` did before the two started colliding.
 *
 * This choice is still SEPARATE from `modelChoicesFor` above — see
 * `currentContextModel` for how the two are reconciled into the one
 * `context.model` a run may carry. The helper model is
 * built at compile time and cannot be switched per run, so it is not offered
 * here.
 */
export type OrchModelChoice = { id: string; label: string; model?: string; badge?: string; reasoning?: ReasoningLevel };

export const ORCH_MODEL_CHOICES: OrchModelChoice[] = withReasoning([
  { id: 'giga2-max', label: 'GigaChat 2 Max', model: 'openai/GigaChat-2-Max', badge: '2 Max' },
  { id: 'giga2-pro', label: 'GigaChat 2 Pro', model: 'openai/GigaChat-2-Pro', badge: '2 Pro' },
  { id: 'giga3-ultra', label: 'GigaChat 3 Ultra', model: 'openai/GigaChat-3-Ultra', badge: 'Ultra' },
  // GPT-5.6 on the dev stand — the backend routes the name to OpenRouter, so
  // the orchestrator's GigaChat endpoint is not where these go.
  ...openrouterChoices().map((choice) => ({ ...choice, badge: choice.label.replace('GPT-5.6 ', 'GPT ') })),
]);

export type GraphChoice = {
  id: string;
  /** The variant's NAME — the menu row, and the header's subtitle. */
  label: string;
  /** One line on how the variant is built, for the header beside the name. */
  description: string;
};

/**
 * What the dev stand's switcher offers — the current generations only. The
 * label and the description are presentation copy and live HERE on purpose
 * (owner, 2026-09-16): the header names the variant in words a visitor of the
 * stand reads, not the graph id, and that text is the frontend's to own rather
 * than another artifact for the deploy to generate.
 */
export const GRAPH_CHOICES: GraphChoice[] = [
  // «Собеседник + помощник» (`peterhof_orch`), «…на Deep Agents»
  // (`peterhof_orch_harness`), «Live» (`peterhof_live`) and «Тандем»
  // (`peterhof_tandem`) left the menu on 2026-10-15 (owner, INV-PH-111): each
  // one's helper ran an open model through the OpenCode gateway, which left the
  // platform, and none was moved onto another model. `peterhof_duo` and
  // `peterhof_canvas` had left the menu earlier and left the registry with them.
  {
    id: 'peterhof_agent',
    label: 'Агент',
    description: 'один агент на GigaChat и Deep Agents: анкета, готовые планы, правки; план помнит между разговорами, доски нет',
  },
  // every build since 20.10.2026, the landing graph (`docs/GUIDE_V2.md`)
  {
    id: 'peterhof_v2',
    label: 'Гид v2',
    description:
      'понять → сделать кодом → сказать: поиск по базе музея на каждом ходе, карточка дня, планы, билеты и афиша кодом',
  },
];

/** The menu row for a graph, or `undefined` for one the switcher does not offer. */
export function graphChoiceFor(assistantId: string): GraphChoice | undefined {
  return GRAPH_CHOICES.find((choice) => choice.id === assistantId);
}

/**
 * Switch the graph: a fresh thread (a checkpoint written by one graph is not
 * another graph's history) and a reload onto `?graph=`, so every store starts
 * clean and the link in the address bar is the one to share.
 */
export function switchGraph(id: string): void {
  if (!KNOWN_GRAPHS.includes(id)) return;
  writeLocal(KEY.thread, uuid4());
  writeLocal(KEY.threadGraph, id);
  const url = new URL(window.location.href);
  url.searchParams.set('graph', id);
  window.location.assign(url.toString());
}

/**
 * The graph a build with no `VITE_PH_DEFAULT_GRAPH` lands on — the Agent, the
 * product's base since 2026-09-22 (every CI build sets it to this anyway). It was
 * `peterhof_canvas` until the canvas left the registry (2026-10-15, INV-PH-111);
 * the scripted guide, reached by `?graph=peterhof_guide` until 2026-10-16, left
 * it too.
 */
export const DEFAULT_ASSISTANT = 'peterhof_agent';

/**
 * Does this graph draw its own board?
 *
 * **The attachments adapter and A2UI are two answers to «what is on the board»,
 * and exactly one of them may speak per graph.** Live on prod (thread
 * `dcc6bbc7`) a tap on «Афиша» drew it TWICE: `EventsList` from the graph's own
 * `custom` frames, and an `ObjectStrip` under it built by the adapter out of the
 * `ui.carousel` attachment that rides the same turn for Telegram to render. The
 * server's checkpoint carried only `events:main`, so the strip was ours.
 *
 * The gate is the GRAPH rather than a race on which frame arrived first: a
 * `values` event can precede the turn's first `custom` one, so «be quiet once
 * A2UI has spoken» leaves the duplicate reachable on the opening frames. Whether
 * a graph has a board is known before the first byte.
 *
 * This is the only place in the frontend that knows these names, which is what
 * `graphFromUrl` above already made true.
 *
 * `peterhof_duo` draws through the SAME code as `peterhof_canvas` — it is the
 * canvas graph with a bridge line spliced in front of the answer, not a fourth
 * board implementation — so it belongs on this side of the gate too.
 *
 * The two ORCHESTRATED graphs (`isOrchGraph`) and `peterhof_tandem` join it for
 * the same reason: each is the canvas graph with a DELEGATION spliced in — a worker runs on its own
 * thread and streams its own `a2ui`/`activity` frames back onto THIS board
 * (`workerStream.ts`) — not a fifth or sixth board implementation.
 *
 * `peterhof_live` (`isLiveGraph`) is a seventh instance of the same fact — its
 * helper is the worker described above, just started without waiting for it.
 */
export function speaksA2ui(assistantId: string): boolean {
  return (
    assistantId === 'peterhof_canvas' ||
    assistantId === 'peterhof_duo' ||
    isOrchGraph(assistantId) ||
    assistantId === 'peterhof_tandem' ||
    isLiveGraph(assistantId)
  );
}

/**
 * Does this graph have no board at all — no `values.attachments` shim, no
 * `.app__board` panel or `MobileTabs` (§plan «Новый агент», 18.09)? Its A2UI
 * frames fill `region:footer` only — suggested replies drawn by `Chips` (#127);
 * never gate `applyFrame`/restore on this, or the chips vanish.
 *
 * **`peterhof_agent` alone**, by owner's decision: the point of the Deep
 * Agents rebuild is a plan the visitor reads and edits entirely in the chat —
 * `TurnCards` renders it in full (`inlineTurnCards.ts`; since 07.10.2026 in the
 * sheet behind the Figma «Моя поездка» card, `chat/TripCard.tsx`), never a preview with
 * a "на доске" button pointing at a panel that does not exist. `App.tsx`
 * reads this to skip mounting the board section and to stop calling
 * `applyAttachments` on this graph's `values` frames — a channel this
 * package's `PlanState` does not even carry.
 */
export { isAgentFamily };

export function hasNoBoard(assistantId: string): boolean {
  return isAgentFamily(assistantId);
}


/**
 * Does this graph read `context.starter` — the start button that began the run
 * (#142)? Only `peterhof_agent` (`graphs/peterhof_plan/starter.py`) opens the
 * button's questionnaire in code; every other graph gets the line alone, with the
 * context shape it always had (`a2ui/actions.ts`'s `tapContext`).
 */
export function readsStarter(assistantId: string): boolean {
  return isAgentFamily(assistantId);
}

/** #76 closing values contract, currently shipped only by the planning graph. */
export function reconcilesFinalAnswer(assistantId: string): boolean {
  return isAgentFamily(assistantId);
}

/**
 * Does the chat show the tool-step feed — `ActivityFeed`, live and collapsed,
 * and `ReasoningBlock` above it?
 *
 * **`peterhof_orch` is the one graph that does not** (user decision,
 * 2026-09-11). Its visitor reads ONE model: the helper's rounds behind
 * `delegate` are a job, not a conversation, and a feed of «Ищу…» steps under a
 * GigaChat answer is the machinery showing. The turn in flight gets one neutral
 * line instead, and what the turn produced is drawn under its answer
 * (`showsTurnCards`). Every other graph keeps the feed exactly as it was.
 *
 * **`peterhof_live` joins it for the same reason, not through `isOrchGraph`.**
 * Its visitor also reads one model at a time — the helper's steps are its own
 * narrator lines (`progressGroups.ts`), not an activity feed of tool calls —
 * and it never ships `turn_cards_mode` (`showsTurnCards` below stays
 * `isOrchGraph`-only), so the two graphs share this ONE reason and diverge on
 * the other.
 */
export function showsActivityFeed(assistantId: string): boolean {
  return !isOrchGraph(assistantId) && !isLiveGraph(assistantId);
}

/**
 * Does the chat draw a turn's cards under that turn's answer (`TurnCards`)?
 *
 * The graph ships them only where `turn_cards_mode` is on — `peterhof_orch` —
 * and this gate is the frontend's half of the same decision, so a snapshot
 * that ever reached another graph's messages would still not change how that
 * graph's chat looks.
 *
 * **`peterhof_agent` joins it (18.09) for the opposite reason `peterhof_orch`
 * has one at all**: the orchestrator has a board and draws the cards under
 * its answer as a CONVENIENCE; the new agent has NO board (`hasNoBoard`), so
 * this is the visitor's only way to see a plan at all. `inlineTurnCards.ts`
 * tells the two apart — the orchestrator's `TripPlan` stays the four-stop
 * preview with a "на доске" button, the agent's renders the full
 * `PlanTimeline` with neither.
 */
export function showsTurnCards(assistantId: string): boolean {
  return isOrchGraph(assistantId) || isAgentFamily(assistantId);
}

function debugFromUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('debug') === '1';
  } catch {
    return false;
  }
}

export type GeoMeta = { source: 'gps' | 'manual' | 'map' | 'object' | 'chat'; selectionId?: string; at: number; accuracyM: number | null; label: string | null };

export type SessionState = {
  userId: string;
  threadId: string;
  assistantId: string;
  modelChoice: string;
  /** `ORCH_MODEL_CHOICES` id — a per-run `context.model` override (orch graphs only). */
  orchModelChoice: string;
  /**
   * A `helperChoicesFor(assistantId)` id — the per-run
   * `context.worker_model_override` of whichever SECOND contour this graph has:
   * the tandem's background worker, or the orchestrators' helper.
   */
  workerModelChoice: string;
  /**
   * What the deploy runs by default, per graph (`transport/api.deployModels`).
   * Loaded once at start-up; `{}` until then and on the public build, which
   * ships no such file — every reader treats a missing entry as «unknown».
   */
  deployModels: DeployModels;
  theme: ThemeChoice;
  geo: { lat: number; lon: number } | null;
  geoState: 'unknown' | 'granted' | 'denied';
  agentGeo: { lat: number; lon: number } | null;
  agentGeoSet: boolean;
  agentGeoMeta: GeoMeta | null;
  agentLocationRevision: number;
  locationResetAt: number | null;
  setAgentGeo: (position: { lat: number; lon: number } | null, meta?: Partial<GeoMeta>) => void;
  /** Renews the time and accuracy of the GPS position already held (`location/autoLocate.ts`): the place, the revision
   *  and the route card stay as they are. A no-op for any other position. */
  touchAgentGeo: (meta: Pick<GeoMeta, 'at' | 'accuracyM'>) => void;
  setChatLocation: (point: { lat: number; lon: number }, meta: Pick<GeoMeta, 'at' | 'label'> & { selectionId: string }) => void;
  debug: boolean;

  newThread: () => void;
  setModelChoice: (id: string) => void;
  setOrchModelChoice: (id: string) => void;
  setWorkerModelChoice: (id: string) => void;
  setDeployModels: (models: DeployModels) => void;
  setTheme: (theme: ThemeChoice) => void;
  setGeo: (position: { lat: number; lon: number } | null, state: 'granted' | 'denied') => void;
  /** Called only AFTER the graph has confirmed the wipe (§5.1). */
  forgetLocally: () => void;
  /**
   * A NEW visitor key after a confirmed erase (#193) — `ph.uid` rotates, the thread stays:
   * the conversation on screen goes on, under a person the store knows nothing about —
   * and nowhere: the place they declared or the GPS gave is dropped with the key.
   * Idempotent per erase id (`ph.forgot`): a reload replaying the same frame does not
   * rotate again. Returns whether it rotated.
   */
  acknowledgeErase: (eraseId: string) => boolean;
};

// Only a reset timestamp survives reload; declared coordinates and labels never do.
function readLocationReset(threadId: string): number | null {
  try {
    const stored = window.sessionStorage.getItem(`ph.locationReset.${threadId}`);
    const value = stored === null ? NaN : Number(stored);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch { return null; }
}
function writeLocationReset(threadId: string, at: number | null): void {
  try {
    const key = `ph.locationReset.${threadId}`;
    if (at === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, String(at));
  } catch { /* unavailable storage must not prevent resetting */ }
}

const initialAssistant = graphFromUrl();
const initialThread = threadFor(initialAssistant);

export const useSession = create<SessionState>((set, get) => ({
  userId: ensure(KEY.uid, () => `web:${uuid4()}`),
  threadId: initialThread,
  assistantId: initialAssistant,
  modelChoice: initialModelChoice(initialAssistant),
  orchModelChoice: readLocal(KEY.orchModel) ?? NO_OVERRIDE,
  workerModelChoice: readLocal(KEY.workerModel) ?? NO_OVERRIDE,
  deployModels: {},
  theme: (readLocal(KEY.theme) as ThemeChoice | null) ?? 'system',
  geo: null,
  agentGeo: null, agentGeoSet: false, agentGeoMeta: null,
  agentLocationRevision: 0, locationResetAt: readLocationReset(initialThread),
  geoState: (readLocal(KEY.geo) as 'granted' | 'denied' | null) ?? 'unknown',
  debug: debugFromUrl(),

  newThread: () => {
    writeLocationReset(get().threadId, null);
    const threadId = uuid4();
    writeLocal(KEY.thread, threadId);
    useNavigation.getState().wipe(get().threadId); // the old chat's map card goes with it, from the screen and the browser
    // `ph.uid` deliberately survives: a new conversation is not a new person.
    set({ threadId, locationResetAt: null, agentLocationRevision: get().agentLocationRevision + 1,
      ...(get().agentGeoMeta?.source === 'chat' ? { agentGeo: null, agentGeoSet: true, agentGeoMeta: null } : {}) });
  },

  setModelChoice: (id) => {
    // Validated against THIS graph's own list and stored under THIS graph's key
    // (`modelKey`, #139) — the same rule as `setWorkerModelChoice` below. The
    // clear removes the key: nothing stored is what «as deployed» looks like.
    const { assistantId } = get();
    if (id !== NO_OVERRIDE && !modelChoicesFor(assistantId).some((choice) => choice.id === id)) return;
    writeLocal(modelKey(assistantId), id === NO_OVERRIDE ? null : id);
    set({ modelChoice: id });
  },

  setOrchModelChoice: (id) => {
    if (id !== NO_OVERRIDE && !ORCH_MODEL_CHOICES.some((choice) => choice.id === id)) return;
    writeLocal(KEY.orchModel, id);
    set({ orchModelChoice: id });
  },

  setWorkerModelChoice: (id) => {
    // `NO_OVERRIDE` is the clear, not a row — the list has no «По умолчанию»
    // item any more, so it has to be accepted here by name.
    //
    // Validated against THIS graph's own list (`helperChoicesFor`), not against
    // one of them: the orchestrators' helper may be a GigaChat and the tandem's
    // worker may not, and a store that accepted either everywhere would let a
    // choice made on one graph ride onto an endpoint that fails on the other.
    if (id !== NO_OVERRIDE && !helperChoicesFor(get().assistantId).some((choice) => choice.id === id)) return;
    writeLocal(KEY.workerModel, id);
    set({ workerModelChoice: id });
  },

  setDeployModels: (models) => set({ deployModels: models }),

  setTheme: (theme) => {
    writeLocal(KEY.theme, theme);
    applyTheme(theme);
    set({ theme });
  },

  setGeo: (position, state) => {
    // The coordinates themselves are never persisted — only whether asking is
    // worth doing again. `docs/UI_CONTRACT.md` §4.2: real geolocation or none.
    writeLocal(KEY.geo, state);
    useNavigation.getState().reset();
    set({ geo: position, geoState: state });
    if (isAgentFamily(get().assistantId)) set({ agentGeoSet: false, agentGeo: null, agentGeoMeta: null, agentLocationRevision: get().agentLocationRevision + 1 });
  },

  setAgentGeo: (position, meta) => {
    if (!isAgentFamily(get().assistantId)) return;
    if (position && (!Number.isFinite(position.lat) || !Number.isFinite(position.lon) || Math.abs(position.lat) > 90 || Math.abs(position.lon) > 180)) return;
    useNavigation.getState().reset();
    const resetAt = position ? get().locationResetAt : Math.max(Date.now(), (get().locationResetAt ?? 0) + 1);
    if (!position) writeLocationReset(get().threadId, resetAt);
    set({ agentGeo: position, agentGeoSet: true, agentGeoMeta: position ? { source: 'manual', at: Date.now(), accuracyM: null, label: null, ...meta } : null,
      agentLocationRevision: get().agentLocationRevision + 1,
      locationResetAt: resetAt });
  },

  touchAgentGeo: (meta) => {
    const current = get().agentGeoMeta;
    if (!get().agentGeo || current?.source !== 'gps') return;
    set({ agentGeoMeta: { ...current, at: meta.at, accuracyM: meta.accuracyM } });
  },

  setChatLocation: (point, meta) => {
    const current = get();
    if (current.agentGeoMeta?.source === 'chat' && current.agentGeoMeta.selectionId === meta.selectionId &&
        current.agentGeoMeta.at === meta.at && current.agentGeoMeta.label === meta.label &&
        current.agentGeo?.lat === point.lat && current.agentGeo.lon === point.lon) return;
    current.setAgentGeo(point, { source: 'chat', ...meta });
  },

  acknowledgeErase: (eraseId) => {
    if (!eraseId || acknowledgedErases.has(eraseId) || readLocal(KEY.forgot) === eraseId) return false;
    acknowledgedErases.add(eraseId);
    writeLocal(KEY.forgot, eraseId);
    writeLocal(KEY.uid, null);
    // the place is the visitor's too (live dev 20.10: after the erase the next turn still rode
    // «метро Ленинский проспект»): dropped like `forgetLocally` does, and since the thread stays,
    // a reset mark tells the server to drop the place its thread holds as well
    writeLocal(KEY.geo, null);
    useNavigation.getState().wipeAll();
    const resetAt = Math.max(Date.now(), (get().locationResetAt ?? 0) + 1);
    writeLocationReset(get().threadId, resetAt);
    set({ userId: ensure(KEY.uid, () => `web:${uuid4()}`), geo: null, geoState: 'unknown', agentGeo: null,
      agentGeoSet: false, agentGeoMeta: null, agentLocationRevision: get().agentLocationRevision + 1,
      locationResetAt: resetAt });
    return true;
  },

  forgetLocally: () => {
    writeLocationReset(get().threadId, null);
    writeLocal(KEY.uid, null);
    writeLocal(KEY.thread, null);
    writeLocal(KEY.geo, null);
    useNavigation.getState().wipeAll();
    const userId = ensure(KEY.uid, () => `web:${uuid4()}`);
    const threadId = ensure(KEY.thread, uuid4);
    set({ userId, threadId, geo: null, geoState: 'unknown', agentGeo: null, agentGeoSet: false, agentGeoMeta: null,
      agentLocationRevision: get().agentLocationRevision + 1, locationResetAt: null });
    void get();
  },
}));

/**
 * The preset for the CURRENT graph, or `undefined` when nothing is chosen —
 * either the store still holds `NO_OVERRIDE`, or it holds a choice made on
 * another graph that this one cannot run (GigaChat 3.5 is the Agent's row
 * only — `modelChoicesFor` differs per graph, so a stale id just does not
 * resolve here). Either way that means no `model` is sent, rather than a
 * request that fails at the gateway.
 */
export function currentModel(): ModelChoice | undefined {
  const { modelChoice, assistantId } = useSession.getState();
  const choices = modelChoicesFor(assistantId);
  return choices.find((choice) => choice.id === modelChoice);
}

/**
 * The orchestrator's `context.model` override for the CURRENT run, or
 * `undefined` when none applies — either «по умолчанию» is chosen, or the
 * graph is not one of the two the override reaches (`isOrchGraph`). A choice
 * made on `peterhof_orch` and left standing while the visitor flips `?graph=`
 * to the canvas must not silently ride along on a graph that never reads it.
 */
export function currentOrchModelOverride(): string | undefined {
  return currentOrchChoice()?.model;
}

/** The pinned `ORCH_MODEL_CHOICES` row itself (its model AND its effort), or `undefined`. */
export function currentOrchChoice(): OrchModelChoice | undefined {
  const { orchModelChoice, assistantId } = useSession.getState();
  if (!usesOrchModelMenu(assistantId)) return undefined;
  return ORCH_MODEL_CHOICES.find((choice) => choice.id === orchModelChoice);
}

/**
 * Which model string, if any, belongs on `context.model` for the CURRENT
 * turn — the one field both switches write to, now that the orchestrator
 * override rides `context` rather than a separate `config.configurable`
 * (reagent refuses the two side by side — see `ORCH_MODEL_CHOICES`'s own
 * docstring). On the two ORCHESTRATOR graphs, `currentOrchModelOverride` wins
 * whenever it is set — it is the dev-only switch built for exactly those
 * graphs, and `currentModel`'s open-model choices would fail at the
 * orchestrator's narrowed endpoint if the two disagreed. Everywhere else, and
 * whenever the orchestrator override is left at «по умолчанию», `currentModel`
 * is unchanged — this is purely a precedence rule between two switches that
 * both happen to write the same field, not a new source of truth.
 */
export function currentContextModel(): string | undefined {
  // On the two orchestrator graphs the open-model choice is NOT a source at
  // all any more (2026-09-16): the menu no longer offers that list there — its
  // second group is the HELPER's model, read-only — and a stale `modelChoice`
  // left over from the canvas would otherwise ride onto the orchestrator's
  // GigaChat gateway as `context.model` and fail every turn.
  if (usesOrchModelMenu(useSession.getState().assistantId)) return currentOrchModelOverride();
  return currentModel()?.model;
}

/**
 * The openai SDK classes whose HTTP status can say «this model is not yours to
 * call», and the statuses that do say it (#140): 401/403 (key), 402 (Ultra on a
 * spent key — the SDK has no class of its own for it, so `APIStatusError`),
 * 404 (no such model). NOT 400 — a bad request can be our own payload, and a
 * silent model switch would hide that bug — and not 429 or a timeout, which pass.
 *
 * **The names reagent actually streams are langchain-openai's**, not the SDK's:
 * `ChatOpenAI` (langchain-openai 1.6, reagent's pin) re-raises 401/403/404 as `OpenAIAuthenticationError` /
 * `OpenAIPermissionDeniedError` / `OpenAIModelNotFoundError` and lets a 402
 * through as the SDK's own `APIStatusError` (live on dev, 2026-09-28: a
 * GigaChat-2-Max turn answered `OpenAIAuthenticationError` «Error code: 401»).
 * The bare SDK names stay for a client that calls the SDK directly.
 */
const DEAD_MODEL_ERRORS = new Set([
  'APIStatusError',
  'OpenAIAuthenticationError',
  'OpenAIPermissionDeniedError',
  'OpenAIModelNotFoundError',
  'AuthenticationError',
  'PermissionDeniedError',
  'NotFoundError',
]);
const DEAD_MODEL_STATUSES = new Set(['401', '402', '403', '404']);

/**
 * Does a run's `error` frame (reagent: the exception's class name and `str(exc)`,
 * which the SDK renders «Error code: 402 - {…}») mean the upstream refused the
 * MODEL? Both halves have to agree: the class alone (`NotFoundError`) is a name
 * other libraries use too, and the status is read off the SDK's own shape only.
 */
export function isDeadModelError(error: string, message: string): boolean {
  if (!DEAD_MODEL_ERRORS.has(error)) return false;
  const status = /^Error code: (\d{3})\b/.exec(message)?.[1];
  return status !== undefined && DEAD_MODEL_STATUSES.has(status);
}

/** What the visitor reads when `dropDeadModelPin` cleared *label*'s pin. */
export function deadModelText(label: string): string {
  return `Модель «${label}» сейчас недоступна — вернул модель по умолчанию. Попробуем ещё раз?`;
}

/**
 * **A pinned model the upstream refuses is dropped, not re-sent on every turn**
 * (#140). *sentModel* is the `context.model` the failed turn carried
 * (`currentContextModel()` when it was sent). When the run failed with
 * `isDeadModelError`, the pin that produced that model — the orchestrator's on
 * the graphs with that menu, this graph's own `ph.model.<graph>` elsewhere — is
 * cleared (`ph.orchModel` is ONE key for orch, harness and live, so a drop
 * there clears it on all three), so the next turn goes out on the deploy's
 * model; returns the row's
 * label for the visitor's message. `undefined`, and nothing changes, when the
 * turn sent no model, the failure is anything else, the model IS the deploy's
 * own (clearing would fall back to the same model), or the visitor has already
 * picked something else while the turn ran. No turn is retried here.
 */
export function dropDeadModelPin(sentModel: string | undefined, error: string, message: string): string | undefined {
  if (!sentModel || !isDeadModelError(error, message)) return undefined;
  if (normalizeModelName(sentModel) === deployDefault('first')) return undefined;
  const { assistantId } = useSession.getState();
  const pinned = usesOrchModelMenu(assistantId) ? currentOrchChoice() : currentModel();
  if (!pinned || pinned.model !== sentModel) return undefined;
  if (usesOrchModelMenu(assistantId)) useSession.getState().setOrchModelChoice(NO_OVERRIDE);
  else useSession.getState().setModelChoice(NO_OVERRIDE);
  // eslint-disable-next-line no-console
  console.warn('[model] pin dropped:', assistantId, sentModel, error, message);
  return pinned.label;
}

/**
 * The deploy's default for one contour of the current graph, normalised to an
 * option id's spelling, or `undefined` when the deploy file has no answer
 * (`transport/api.deployModels` — absent on the public build, and empty until
 * it has loaded).
 */
export function deployDefault(which: 'first' | 'second'): string | undefined {
  const { deployModels, assistantId } = useSession.getState();
  return normalizeModelName(deployModels[assistantId]?.[which]);
}

/**
 * The provider's own model string (`run_meta.model` once the actual answering
 * message has arrived — e.g. `"GigaChat-2-Max:2.0.28.2"`, `chatStore.
 * modelInUse`'s late value) normalised to an `OrchModelChoice`/`ModelChoice`
 * id — dropping the `:version` tail and adding the `openai/` prefix our own
 * option ids all carry when the raw string lacks one (an id we sent
 * ourselves already has it, since that is the literal string of `context.
 * model`). `undefined` for an empty/unset string, never a guess.
 */
export function normalizeModelName(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const base = raw.split(':')[0]?.trim();
  if (!base) return undefined;
  return base.startsWith('openai/') ? base : `openai/${base}`;
}

/**
 * Which `ORCH_MODEL_CHOICES` item the settings menu should CHECK right now —
 * the override if one is pinned, else whichever option matches the
 * orchestrator's own `run_meta.model` (normalised), else the DEPLOY's default
 * for the first contour (`deployDefault`). `undefined` only when none of the
 * three matches a row — the menu then shows no checkmark rather than guessing
 * (owner's request, 2026-09-16). The order is the whole point: a pin is what
 * the visitor asked for, `run_meta` is what actually ran, and the deploy file
 * is the honest answer on a fresh conversation, where the first two do not
 * exist yet — which is exactly where the menu used to show nothing.
 */
export function orchChoiceIdFor(runMetaModel: string | null | undefined): string | undefined {
  if (!usesOrchModelMenu(useSession.getState().assistantId)) return undefined;
  const pinned = currentOrchChoice();
  if (pinned) return pinned.id;
  return rowFor(ORCH_MODEL_CHOICES, normalizeModelName(runMetaModel) ?? deployDefault('first'), 'first');
}

/**
 * The row of *choices* that runs *model* — and, where the model is listed once
 * per effort (`withReasoning`), the one at the DEPLOY's effort for that contour
 * when the deploy runs this very model. No such effort known → no row: an
 * effort is never guessed, the checkmark is simply absent.
 */
export function rowFor(
  choices: ModelChoice[],
  model: string | undefined,
  which: 'first' | 'second',
): string | undefined {
  if (!model) return undefined;
  const rows = choices.filter((choice) => choice.model === model);
  if (rows.length <= 1) return rows[0]?.id;
  const { deployModels, assistantId } = useSession.getState();
  const effort = deployDefault(which) === model ? deployReasoning(which, deployModels[assistantId]) : undefined;
  return rows.find((choice) => choice.reasoning === effort)?.id;
}

/**
 * The FIRST-contour row the deploy runs when nothing is pinned — the row whose tap
 * CLEARS a pin (`SettingsPanel`). The panel also learns it from the checkmark while
 * no pin is active, but a page that opens WITH a pin never sees that stretch: a
 * pin left from the old list (23.10.2026: «3 Ultra» on Guide v2) or a pin on the
 * default row itself turned the tap on 3.5 into one more pin, and nothing cleared it.
 */
export function deployDefaultChoiceId(choices: ModelChoice[]): string | undefined {
  return rowFor(choices, deployDefault('first'), 'first');
}

/**
 * Same question for the FIRST-contour switch of every other graph
 * (`CLOUDRU_MODEL_CHOICES`/`GIGACHAT_MODEL_CHOICES`, whichever `modelChoicesFor`
 * returned): the visitor's own choice if one is pinned, else whichever option
 * matches `runMetaModel` (normalised) — the model that ACTUALLY ran — else the
 * deploy's default for the first contour. `undefined` when nothing matches: a
 * deploy string the list does not carry is never «snapped» to the nearest row.
 */
export function modelChoiceIdFor(choices: ModelChoice[], runMetaModel: string | null | undefined): string | undefined {
  const pinned = currentModel();
  if (pinned && choices.some((choice) => choice.id === pinned.id)) return pinned.id;
  return rowFor(choices, normalizeModelName(runMetaModel) ?? deployDefault('first'), 'first');
}

/**
 * The SECOND contour's model as the visitor should read it — the helper on
 * the two orchestrator graphs, the background worker on the tandem: what the
 * thread's `run_meta.worker_model` reported if it has, else the deploy's
 * default for the second contour. A string, not a row id, because the deploy
 * may run a model no list carries — it is then SHOWN in the group's heading
 * rather than snapped to the nearest row. `undefined` when neither source knows.
 *
 * `run_meta.worker_model` is the EFFECTIVE model of the run on all three graphs,
 * the per-run override included, so this and the checkmark cannot disagree with
 * what actually ran.
 */
export function workerModelInUseFor(runMetaWorkerModel: string | null | undefined): string | undefined {
  return normalizeModelName(runMetaWorkerModel) ?? deployDefault('second');
}

/**
 * Which row the second-contour group should CHECK: the pinned override, else the
 * row matching what runs (`workerModelInUseFor`). `undefined` on a graph with no
 * second contour, or when the running model is not one of that graph's rows —
 * the deploy's own helper is then NAMED in the group's heading instead, never
 * snapped to the nearest row.
 */
export function workerChoiceIdFor(runMetaWorkerModel: string | null | undefined): string | undefined {
  const { workerModelChoice, assistantId } = useSession.getState();
  const choices = helperChoicesFor(assistantId);
  if (!choices.length) return undefined;
  const pinned = choices.find((choice) => choice.id === workerModelChoice);
  if (pinned) return pinned.id;
  return rowFor(choices, workerModelInUseFor(runMetaWorkerModel), 'second');
}

/**
 * The SECOND contour's model for the current turn, or `undefined` when none
 * applies — nothing is pinned, the pin is a row this graph cannot run, or the
 * graph has no second circuit to point anywhere (`helperChoicesFor`).
 *
 * It reaches `peterhof_orch`/`peterhof_orch_harness` too since 2026-09-16: their
 * helper used to be built once with the graph, and it is now built per model
 * spec and cached, with the ENDPOINT resolved from the name
 * (`peterhof_orch.tools.worker_endpoint`) — which is what lets one list offer
 * both families.
 *
 * It rides its OWN field (`context.worker_model_override`) rather than joining
 * the reconciliation above, because `context.model` is already spoken for by the
 * contour the visitor is reading — the two models of this graph are two facts,
 * and folding them into one field is how a fast-contour GigaChat id would end
 * up at the worker's open-model endpoint.
 *
 * **The name carries an `_override` suffix on purpose**: the graph already has
 * a `worker_model` — the DEPLOY's claim for the header — so a run sending that
 * key would be indistinguishable from the default, and emptiness is exactly
 * what tells the graph to leave `PW_MODEL` alone. The first cut sent the bare
 * name and tried to detect «did the run set it?» on the backend; that check was
 * false on every live turn, and the header named a model the worker was not
 * running (`peterhof_tandem.context.Context.worker_model_override`).
 */
export function currentWorkerModel(): string | undefined {
  const { workerModelChoice, assistantId } = useSession.getState();
  return helperChoicesFor(assistantId).find((choice) => choice.id === workerModelChoice)?.model;
}

export type ReasoningWire = 'none' | 'off' | 'low' | 'medium' | 'high';

/**
 * A row's effort in the wire's spelling. «Off» is spelled by the GRAPH:
 * `peterhof_agent` (package `peterhof_plan`) takes `off`, every other graph
 * `none` — and it is SENT, never left out, because GPT-5.6 reasons at
 * `medium` when told nothing.
 */
export function reasoningWire(choice: string, assistantId: string): ReasoningWire | undefined {
  if (!REASONING_CHOICES.some((option) => option.id === choice)) return undefined;
  if (choice === 'off') return assistantId === 'peterhof_agent' ? 'off' : 'none';
  return choice as ReasoningWire;
}

/**
 * `context.reasoning_effort` (the FIRST contour) for the current run: the
 * effort of the pinned MODEL row, or `undefined` — nothing pinned, or a model
 * without the parameter — which leaves the deploy's own effort in charge.
 */
export function currentReasoningEffort(): ReasoningWire | undefined {
  const { assistantId } = useSession.getState();
  const row = usesOrchModelMenu(assistantId) ? currentOrchChoice() : currentModel();
  return row?.reasoning ? reasoningWire(row.reasoning, assistantId) : undefined;
}

/**
 * `context.worker_reasoning_effort` (the SECOND contour) — the effort of the
 * pinned helper/worker row. Always the `none` spelling: no second contour runs
 * `peterhof_plan`.
 */
export function currentWorkerReasoningEffort(): ReasoningWire | undefined {
  const { workerModelChoice, assistantId } = useSession.getState();
  const row = helperChoicesFor(assistantId).find((choice) => choice.id === workerModelChoice);
  return row?.reasoning ? reasoningWire(row.reasoning, '') : undefined;
}

/** The deploy's effort for one contour in a level's spelling, if it is one. */
export function deployReasoning(
  which: 'first' | 'second',
  entry: DeployModels[string] | undefined,
): ReasoningLevel | undefined {
  const raw = which === 'first' ? entry?.first_reasoning : entry?.second_reasoning;
  if (!raw) return undefined;
  const id = raw === 'none' ? 'off' : raw;
  return REASONING_CHOICES.find((option) => option.id === id)?.id;
}

/** `data-theme` beats the media query in both directions; «system» clears it. */
export function applyTheme(theme: ThemeChoice): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}
